import { useMemo } from "react";

import { normalizeDomain } from "app/pages/digital-marketing/utils/validation/url/domainNormalization";
import { isValidDomainOrURL } from "app/pages/digital-marketing/utils/validation/url/domainValidation";

import type { IDomainSearchResult, IRecentDomain } from "../../../services/domain-search";
import { useDomainSuggestions, useRecentDomains } from "../../../services/domain-search";

import type { IDomainSuggestion } from "./domain-input.types";

const MAX_DOMAIN_OPTIONS = 10;

interface IUseDomainOptionsParams {
    searchTerm: string;
    maxOptions?: number;
}

interface IDomainOptions {
    recents: IDomainSuggestion[];
    suggestions: IDomainSuggestion[];
    /** The "use exactly what I typed" option, or null when the term is not a usable domain. */
    customOption: IDomainSuggestion | null;
    isLoading: boolean;
    isSearching: boolean;
    isEmpty: boolean;
}

const toSuggestions = (
    results: readonly (IDomainSearchResult | IRecentDomain)[],
    maxOptions: number,
): IDomainSuggestion[] => {
    const seenDomains = new Set<string>();

    return results.reduce<IDomainSuggestion[]>((options, result) => {
        const domain = normalizeDomain(result.name);

        if (domain && !seenDomains.has(domain) && options.length < maxOptions) {
            seenDomains.add(domain);
            options.push({ domain, iconUrl: result.image });
        }

        return options;
    }, []);
};

/**
 * Turns the raw autocomplete and recent-analysis responses into normalized, de-duplicated options,
 * plus the free-text option that lets a user commit a domain the autocomplete does not know.
 */
export const useDomainOptions = ({
    searchTerm,
    maxOptions = MAX_DOMAIN_OPTIONS,
}: IUseDomainOptionsParams): IDomainOptions => {
    const trimmedTerm = searchTerm.trim();
    const isSearching = trimmedTerm.length > 0;

    const { data: recentDomains = [], isLoading: isRecentsLoading } = useRecentDomains();
    const { suggestions: searchResults, isLoading: isSuggestionsLoading } = useDomainSuggestions({ term: trimmedTerm });

    const recents = useMemo(
        () => (isSearching ? [] : toSuggestions(recentDomains, maxOptions)),
        [isSearching, maxOptions, recentDomains],
    );

    const suggestions = useMemo(
        () => (isSearching ? toSuggestions(searchResults, maxOptions) : []),
        [isSearching, maxOptions, searchResults],
    );

    const customOption = useMemo(() => {
        const domain = normalizeDomain(trimmedTerm);

        if (!domain || !isValidDomainOrURL(domain) || suggestions.some((option) => option.domain === domain)) {
            return null;
        }

        return { domain };
    }, [suggestions, trimmedTerm]);

    const isLoading = isSearching ? isSuggestionsLoading : isRecentsLoading;

    return {
        recents,
        suggestions,
        customOption,
        isLoading,
        isSearching,
        isEmpty: !isLoading && !recents.length && !suggestions.length && !customOption,
    };
};
